import React, { createContext, useEffect, useState } from 'react'
import { getPoints, rewardPoints } from 'services/points'
import { updateProfile, userTypeEnum } from 'services/user'
import { initUserMetaData } from 'services/track'
import analytics from 'global/AnalyticsData'
import localStorageWrapper from 'util/LocalStorageWrapper'
import { isEqual } from 'lodash'
import { getUtmParameters, saveUtmParameters } from 'util/utm'
import { protectedRoutes } from '../config/protected-routes'
import { globalHistory } from '@reach/router'
import { navigate } from 'gatsby'

const guestProfile = {
  name: 'Guest',
  isLoggedIn: false,
  points: 0,
  guest: true,
}

const notReplacableUtmParams = {
  utm_campaign: [],
  utm_content: ['AMOE'],
  utm_medium: [],
  utm_source: [],
  utm_term: [],
}

const isBrowser = () => typeof window !== 'undefined'

export const getProfile = () => {
  // we don't need context for profile - it's in local storage
  return isBrowser() && localStorageWrapper.getItem('dailybreakUser')
    ? JSON.parse(localStorageWrapper.getItem('dailybreakUser'))
    : guestProfile
}

export const getUserType = () => {
  const profile = getProfile()

  if (profile.isLoggedIn === true) {
    if (profile.guest) {
      return userTypeEnum.GUEST
    } else {
      return userTypeEnum.REGISTERED
    }
  } else {
    return userTypeEnum.NONE
  }
}

//Initialize context props so gatsby is happy when we try to destructure later
// TODO: userContext should be UserContext
const userContext = createContext({
  getProfile: function () {
    // we don't need context for profile - it's in local storage
    return isBrowser() && localStorageWrapper.getItem('dailybreakUser')
      ? JSON.parse(localStorageWrapper.getItem('dailybreakUser'))
      : guestProfile
  },
  setProfile: function () {},
  getSweepsEntries: function () {},
  setSweepsEntries: function () {},
  isLoggedIn: function () {
    return false
  },
  points: 0,
  setPoints: function (points = 0) {},
  askToLogin: function (tab = 0) {},
  unauthorized: function () {},
  logout: function () {},
  isLoggedInBoolean: false,
  isGuest: true,
  authDialogOpen: false,
  authDialogTab: 0,
  askToSignIn: function () {},
  closeAuthDialog: function () {},
  onAuthSuccess: undefined,
  setOnAuthSuccess: function (fn) {},
  completeProfileDialogOpen: false,
  askToCompleteProfile: function () {},
  closeCompleteProfileDialog: function () {},
})

// when we first load up, try reading profile from local storage
// the user might have a cookie from a previous session
const initProfile = () => {
  return isBrowser() && localStorageWrapper.getItem('dailybreakUser')
    ? JSON.parse(localStorageWrapper.getItem('dailybreakUser'))
    : guestProfile
}

const UserProvider = ({ children }) => {
  const { Provider } = userContext
  const [internalProfile, setInternalProfile] = useState(guestProfile)
  const [points, setPoints] = useState(guestProfile.points)
  const [sweepsEntries, setSweepsEntries] = useState()
  // BR-3138: sign-in/up dialog open state lives here now (was modalContext's
  // signInUpModal boolean + modalContent.args.tab). SignInUpDialog is mounted
  // in Layout.jsx and driven by this state.
  const [authDialog, setAuthDialog] = useState({ open: false, tab: 0 })
  const [onAuthSuccess, setOnAuthSuccess] = useState()
  // BR-3138 (section 6): complete-profile dialog open state, analogous to
  // authDialog above. Mounted alongside SignInUpDialog in Layout.jsx so it
  // survives the auth dialog closing - SignInUpForm calls askToCompleteProfile()
  // right after a successful registration closes the auth dialog.
  const [completeProfileDialogOpen, setCompleteProfileDialogOpen] =
    useState(false)

  // need to wait until browser/session storage is available to
  useEffect(() => {
    let tmp = initProfile()
    setInternalProfile(tmp)
    initUserMetaData(tmp)
    setPoints(tmp.points)
  }, [])

  // gets called after login by the login modal.
  const setProfile = profile => {
    if (isBrowser()) {
      profile.isLoggedIn = true
      profile.name = profile.firstName
      // also hang onto this in session storage
      localStorageWrapper.setItem('dailybreakUser', JSON.stringify(profile))

      setInternalProfile(profile)
      setPoints(parseInt(profile.points))
    }
  }

  const getSweepsEntries = () => {
    return sweepsEntries
  }

  const isLoggedIn = () => {
    return internalProfile.isLoggedIn
  }

  const handlePoints = points => {
    rewardPoints(points)
    setPoints(getPoints())
  }

  // tab is optional and ignorable by existing callers: tab 0 (sign-up) is the
  // default, matching the old askToLogin -> showSignUpModalTab() behavior.
  const askToLogin = (tab = 0) => {
    setAuthDialog({ open: true, tab })
  }

  const askToSignIn = () => {
    setAuthDialog({ open: true, tab: 1 })
  }

  const closeAuthDialog = () => {
    setAuthDialog(prev => ({ ...prev, open: false }))
  }

  const askToCompleteProfile = () => {
    setCompleteProfileDialogOpen(true)
  }

  const closeCompleteProfileDialog = () => {
    setCompleteProfileDialogOpen(false)
  }

  // user tried doing something and a service declined
  const unauthorized = () => {
    if (internalProfile.isLoggedIn !== false) {
      logout()
    }
    // else no need to update
  }

  const logout = () => {
    setInternalProfile(guestProfile)
    analytics.setUser({})

    if (isBrowser()) {
      localStorageWrapper.removeItem('dailybreakUser')
      localStorageWrapper.removeItem('accesToken')
    }

    // todo: clean up any local storage / session storage from steps
  }

  const getParamsToUpdate = (internalProfile, utmParametersSaved) => {
    const paramsToUpdate = {}

    Object.keys(notReplacableUtmParams).forEach(key => {
      if (notReplacableUtmParams[key].includes(internalProfile[key])) {
        paramsToUpdate[key] = internalProfile[key]
      } else {
        // I had to do this to overwrite values if some come null
        paramsToUpdate[key] = utmParametersSaved?.[key] || null
      }
    })
    return paramsToUpdate
  }

  const checkUTMParameters = async () => {
    const rawUtmParametersSaved = (await getUtmParameters())?.parameters

    const utmParametersSaved = getParamsToUpdate(
      internalProfile,
      rawUtmParametersSaved,
    )

    const { utm_campaign, utm_content, utm_medium, utm_source, utm_term } =
      internalProfile

    if (
      isEqual(utmParametersSaved, {
        utm_campaign,
        utm_content,
        utm_medium,
        utm_source,
        utm_term,
      })
    ) {
      return
    } else {
      // check first if I have utm parameters in the saved session
      if (utmParametersSaved) {
        setProfile({
          utm_campaign: utmParametersSaved?.utm_campaign,
          utm_content: utmParametersSaved?.utm_content,
          utm_medium: utmParametersSaved?.utm_medium,
          utm_source: utmParametersSaved?.utm_source,
          utm_term: utmParametersSaved?.utm_term,
        })
        if (!internalProfile.guest) {
          updateProfile(utmParametersSaved)
        }
      } else {
        // in this case I don't have utm parameters in the saved session
        // so I need to save them from the user
        await saveUtmParameters({
          utm_campaign: internalProfile?.utm_campaign,
          utm_content: internalProfile?.utm_content,
          utm_medium: internalProfile?.utm_medium,
          utm_source: internalProfile?.utm_source,
          utm_term: internalProfile?.utm_term,
        })
      }
    }
  }

  // check if the user is logged in and perform some actions
  useEffect(() => {
    if (internalProfile.isLoggedIn) {
      checkUTMParameters()
    }
  }, [internalProfile.isLoggedIn])

  const checkProtectedRoutes = () => {
    const isProtectedRoute = protectedRoutes.includes(window.location.pathname)

    if (
      !isProtectedRoute ||
      (isProtectedRoute && localStorageWrapper.getItem('dailybreakUser'))
    )
      return

    navigate('/')
  }

  useEffect(() => {
    checkProtectedRoutes()

    return globalHistory.listen(({ action }) => {
      if (['PUSH', 'POP'].includes(action)) checkProtectedRoutes()
    })
  }, [])

  return (
    <Provider
      value={{
        getProfile: getProfile,
        setProfile: setProfile,
        getSweepsEntries: getSweepsEntries,
        setSweepsEntries: setSweepsEntries,
        points: points,
        rewardPoints: handlePoints,
        setPoints: setPoints,
        isLoggedIn: isLoggedIn,
        askToLogin: askToLogin,
        unauthorized: unauthorized,
        logout: logout,
        isLoggedInBoolean: internalProfile.isLoggedIn,
        isGuest: internalProfile.guest,
        authDialogOpen: authDialog.open,
        authDialogTab: authDialog.tab,
        askToSignIn: askToSignIn,
        closeAuthDialog: closeAuthDialog,
        onAuthSuccess: onAuthSuccess,
        setOnAuthSuccess: setOnAuthSuccess,
        completeProfileDialogOpen: completeProfileDialogOpen,
        askToCompleteProfile: askToCompleteProfile,
        closeCompleteProfileDialog: closeCompleteProfileDialog,
      }}
    >
      {children}
    </Provider>
  )
}

export { userContext, UserProvider }
